/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faArrowRotateRight } from '@fortawesome/free-solid-svg-icons/faArrowRotateRight';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faCommentDots } from '@fortawesome/free-solid-svg-icons/faCommentDots';
import { faComments } from '@fortawesome/free-solid-svg-icons/faComments';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle';
import { faTasks } from '@fortawesome/free-solid-svg-icons/faTasks';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { BehaviorSubject, fromEvent, Observable, of, Subscription, throwError } from 'rxjs';
import { distinctUntilKeyChanged, filter, mergeMap, skip, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { HelperActivityService } from '../../../services/helper/helper-activity/helper-activity.service';
import { ActivityInterface } from '../../../models/interface/activity.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { MessageThreadsInterface } from '../../../models/interface/message_threads.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { UserActionTypes } from '../../../store/user/user.action';
import { MessageStore } from '../../../store/message/component-store/message.store';
import { HttpResponse } from '@angular/common/http';
import { UserStore } from '../../../store/user/component-store/user.store';
import { CustomCookieService } from 'src/app/services/customcookie/custom-cookie.service';
import { UserSharedService } from 'src/app/services/shared/user-shared/user-shared.service';

/**
 * Home component displaying the starting page of eCoach platform
 */
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  providers: [MessageStore, UserStore],
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('instanceDetailContainer', { read: ViewContainerRef })
  instanceDetailContainer: ViewContainerRef;

  // Icons
  faTasks = faTasks;
  faComments = faComments;
  faPen = faPen;
  faCommentDots = faCommentDots;
  faClock = faClock;
  faPlusCircle = faPlusCircle;
  faInfoCircle = faInfoCircle;
  faArrowRight = faArrowRight;
  faArrowRotateRight = faArrowRotateRight;
  faUser = faUser;

  public isLoadingTasks$: Observable<boolean>;
  public isLoadingActivities$: Observable<boolean>;
  public isLoadingFeedback$: Observable<boolean>;
  public isLoadingConversations$: Observable<boolean>;

  private threadsParticipants: MessageThreadsInterface[] = [];
  unreadConversations = 0;
  private feedbackForParticipants: MessageThreadsInterface[] = [];
  unreadFeedback = 0;

  // Data provided by UserService
  public members: UserInterface[] = [];

  public tasksSubject = new BehaviorSubject<ActivityInterface[]>([]);
  public memberActivitiesSubject = new BehaviorSubject<ActivityInterface[]>([]);

  public threads$: Observable<MessageThreadsInterface[]>;
  public feedback$: Observable<MessageThreadsInterface[]>;

  public pagedActivitySubject = new BehaviorSubject<ActivityInterface[]>([]);
  public pagedActivity$: Observable<ActivityInterface[]> = this.pagedActivitySubject.asObservable();

  public pagedTaskSubject = new BehaviorSubject<ActivityInterface[]>([]);
  public pagedTask$: Observable<ActivityInterface[]> = this.pagedTaskSubject.asObservable();

  // Translation for intervention name
  public param = { intervention_name: '...' };

  public profile$: Observable<ProfileInterface>;
  public myActivities$: Observable<ActivityInterface[]>;

  private isLoadingTasksSubject = new BehaviorSubject<boolean>(true);
  private isLoadingActivitiesSubject = new BehaviorSubject<boolean>(true);
  private isLoadingFeedbackSubject = new BehaviorSubject<boolean>(true);
  private isLoadingConversationsSubject = new BehaviorSubject<boolean>(true);

  private getThreadDetailsResponse$: Observable<any>;

  private myMembers$: Observable<UserInterface[]>;
  private memberTasks$: Observable<ActivityInterface[]>;
  private studyActivities$: Observable<ActivityInterface[]>;

  private memberActivities: ActivityInterface[] = [];
  private myTasks: ActivityInterface[] = [];

  private feedbackSubject = new BehaviorSubject<MessageThreadsInterface[]>([]);
  private threadsSubject = new BehaviorSubject<MessageThreadsInterface[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private router: Router,
    private helperActivityService: HelperActivityService,
    private helperDialogService: HelperDialogService,
    private translateService: TranslateService,
    private customCookieService: CustomCookieService,
    private userSharedService: UserSharedService,
    private messageStore: MessageStore,
    private userStore: UserStore,
    private store: Store<{
      myProfile: ProfileInterface;
      myActivities: ActivityInterface[];
      myMembers: UserInterface[];
    }>,
  ) {
    this.profile$ = this.store.select('myProfile');
    this.myActivities$ = this.store.select('myActivities');
    this.myMembers$ = this.store.select('myMembers');
    this.isLoadingTasks$ = this.isLoadingTasksSubject.asObservable();
    this.isLoadingActivities$ = this.isLoadingActivitiesSubject.asObservable();
    this.isLoadingFeedback$ = this.isLoadingFeedbackSubject.asObservable();
    this.isLoadingConversations$ = this.isLoadingConversationsSubject.asObservable();
    this.threads$ = this.threadsSubject.asObservable();
    this.feedback$ = this.feedbackSubject.asObservable();
    this.getThreadDetailsResponse$ = this.messageStore.getThreadDetailsResponse$;
    this.memberTasks$ = this.userStore.memberTasks$;
    this.studyActivities$ = this.userStore.studyActivities$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  public get helperActivity() {
    return this.helperActivityService;
  }

  ngOnInit(): void {
    const source$ = fromEvent<StorageEvent>(window, 'storage');
    this.subscriptions.push(
      source$
        .pipe(
          filter((value) => value.key === 'isAuthenticated'),
          distinctUntilKeyChanged('newValue'),
          switchMap((_value: StorageEvent) => {
            if (this.customCookieService.isCookieValid()) {
              this.initialize();
            }
            return of(true);
          })
        )
        .subscribe({})
    );

    this.initialize();

    this.subscriptions.push(
      this.translateService.onLangChange
        .pipe(
          mergeMap((_event: LangChangeEvent) => {
            this.isLoadingTasksSubject.next(true);
            this.isLoadingActivitiesSubject.next(true);
            this.memberActivities = [];
            this.myTasks = [];
            this.userStore.getTasksOfMembers({
              activityFilter: 'ANSWERSHEET_SUBMITTED,INTERVENTION_INSTANCE_CAN_UNLOCK',
              finished: '0,pending',
              feedbackRequired: 1,
              include: 'questionnaires',
            });
            return this.memberTasks$.pipe(skip(1), take(1));
          }),
        )
        .pipe(
          mergeMap((tasks) => {
            this.setupTasks(tasks);
            this.isLoadingTasksSubject.next(false);
            this.userStore.getActivitiesOfMembers({ include: 'questionnaires' });
            return this.studyActivities$.pipe(skip(1), take(1));
          })
        )
        .subscribe({
          next: (activities) => {
            this.setupMemberActivities(activities);
            this.isLoadingActivitiesSubject.next(false);
          },
          error: (error) => {
            console.error(error);
          },
        })
    );

    this.subscriptions.push(
      this.myMembers$.subscribe((members) => {
        this.members = members;
      })
    );

    this.subscriptions.push(
      this.studyActivities$.pipe(skip(1), take(1)).subscribe((activities) => {
        this.setupMemberActivities(activities);
        this.isLoadingActivitiesSubject.next(false);
      })
    );

    this.subscriptions.push(
      this.userSharedService.conversations$
        .pipe(
          filter((conversations) => conversations !== null),
        )
        .subscribe({
          next: (conversations) => {
            this.threadsParticipants = conversations.filter((thread) => thread.attributes?.answersheet_id === null);
            const unreadThreads = this.helper.getInboxWithNewestUnreadMessage(this.threadsParticipants);
            this.unreadConversations = unreadThreads.length;
            this.threadsSubject.next(unreadThreads);
            this.isLoadingConversationsSubject.next(false);
          },
          error: (error) => {
            throwError(() => error);
          },
        }),
    );

    this.subscriptions.push(
      this.userSharedService.feedback$
        .pipe(
          filter((feedback) => feedback !== null),
          mergeMap((feedback) => {
            this.feedbackForParticipants = feedback.filter((thread) => thread.attributes?.answersheet_id !== null);
            const unreadFeedback = this.helper.getInboxWithNewestUnreadMessage(this.feedbackForParticipants);
            this.unreadFeedback = unreadFeedback.length;
            this.feedbackSubject.next(unreadFeedback);
            this.isLoadingFeedbackSubject.next(false);
            return this.userSharedService.tasks$.pipe(filter((tasks) => tasks !== null));
          }),
        )
        .subscribe({
          next: (tasks) => {
            this.setupTasks(tasks);
            this.isLoadingTasksSubject.next(false);
          },
          error: (error) => {
            throwError(() => error);
          },
        }),
    );
  }

  private setupTasks(tasks: ActivityInterface[]) {
    this.myTasks = tasks;
    const pendingTasks = this.helperActivity.filterPendingTasks(this.myTasks, this.feedbackForParticipants);
    this.tasksSubject.next(this.helper.getActivityByNewest(pendingTasks));
    this.pagedTaskSubject.next(this.tasksSubject.value.slice(0, 20));
  }

  private setupMemberActivities(activities: ActivityInterface[]) {
    this.memberActivities = activities;
    this.memberActivitiesSubject.next(this.helper.getActivityByNewest(this.memberActivities));
    this.pagedActivitySubject.next(this.memberActivitiesSubject.value.slice(0, 20));
  }

  // Navigate to answersheet
  public showAnswersheet(activity: ActivityInterface): void {
    const answersheetId = this.helper.getAnswersheetIdFromActivity(activity);
    this.router.navigateByUrl(`/feedback-overview/answersheets/${answersheetId}`);
  }

  public getAnswersheetDetails(thread: MessageThreadsInterface): void {
    this.messageStore.getThreadDetails({ threadId: thread.id });
    this.subscriptions.push(
      this.getThreadDetailsResponse$.pipe(skip(1), take(1)).subscribe(result => {
        if (result instanceof HttpResponse) {
          this.router.navigateByUrl(`feedback-overview/answersheets/${thread.attributes.answersheet_id}`);
        }
      })
    );
  }

  public updatePagedActivities(event: ActivityInterface[]) {
    if (event) {
      this.pagedActivitySubject.next(event);
    }
  }

  public updatePagedTasks(event: ActivityInterface[]) {
    if (event) {
      this.pagedTaskSubject.next(event);
    }
  }

  public openDialogInterventionInstanceDetails(activity: ActivityInterface): void {
    const instanceId =
      activity.attributes.uri !== undefined ? this.helper.getLastIndexIDOfURI(activity.attributes.uri) : activity.attributes.entity_id;
    this.helperDialog
      .openDialogInterventionInstanceDetails(instanceId, this.param, 1)
      .afterClosed()
      .subscribe(() => {});
  }

  public initialize() {
    this.store.dispatch({
      type: UserActionTypes.getMyMembersType,
      payload: {}
    });
    this.userStore.getActivitiesOfMembers({ include: 'questionnaires' });
  }

  reloadPage() {
    window.location.reload();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
